import { useSearchParams } from 'react-router';
import { JOB_KINDS, JOB_STATES } from '@devrobot/shared';
import { ListChecks, Server, X } from 'lucide-react';
import { useJobs } from '../../api/queries';
import { ErrorAlert } from '../../components/ErrorAlert';
import { JobsTable } from '../../components/JobsTable';
import { IconChip, PageHeader } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';

export default function JobsPage() {
  const [params, setParams] = useSearchParams();
  const active = params.get('active') === '1';
  const kind = params.get('kind') ?? '';
  const state = params.get('state') ?? '';
  const vps = params.get('vps') ?? '';
  const q = useJobs({ active, kind: kind || undefined, state: state || undefined, vps: vps || undefined, limit: 100 });

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        icon={<IconChip icon={ListChecks} tone="primary" size="lg" />}
        title="Deployments & jobs"
        subtitle="Every server-side operation: deployments, lifecycle actions, configuration applies, backups, restores and VPS enrollment. Jobs keep running if you close this page."
      />
      <form className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3" onSubmit={(e) => e.preventDefault()} aria-label="Filter jobs">
        <label className="flex h-8 cursor-pointer items-center gap-2 rounded-field border border-hairline bg-base-100 px-3 text-sm">
          <input type="checkbox" className="toggle toggle-xs toggle-primary" checked={active} onChange={(e) => set('active', e.target.checked ? '1' : '')} />
          Active only
        </label>
        <select className="select select-sm w-auto" aria-label="Kind" value={kind} onChange={(e) => set('kind', e.target.value)}>
          <option value="">All kinds</option>
          {JOB_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select className="select select-sm w-auto" aria-label="State" value={state} onChange={(e) => set('state', e.target.value)}>
          <option value="">All states</option>
          {JOB_STATES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
        {vps ? (
          <span className="badge badge-soft badge-secondary h-8 gap-1.5 pr-1">
            <Server className="size-3.5" aria-hidden="true" />
            VPS filter
            <button type="button" className="btn btn-circle btn-ghost btn-xs" aria-label="Remove VPS filter" onClick={() => set('vps', '')}>
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        ) : null}
        {active || kind || state || vps ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setParams({}, { replace: true })}>
            Clear filters
          </button>
        ) : null}
        {q.isFetching && !q.isPending ? <span className="loading loading-spinner loading-xs text-base-content/50 sm:ml-auto" aria-label="Refreshing" /> : null}
      </form>
      {q.isPending ? <SkeletonTable /> : null}
      {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data ? (
        <JobsTable variant="card"
          jobs={q.data}
          emptyTitle={active || kind || state ? 'No jobs match these filters' : 'No jobs yet'}
          emptyText={active || kind || state ? 'Clear the filters to see all jobs.' : 'Start a deployment from a project’s Deploy tab, or with devrobot deploy from the CLI.'}
        />
      ) : null}
    </>
  );
}
